import { flexRender, type RowData } from '@tanstack/react-table';

import {
  DimeTable,
  DimeTableBody,
  DimeTableCell,
  DimeTableHead,
  DimeTableHeader,
  DimeTableRow,
  SkeletonRows,
} from '../DimeTable/index.js';
import { cn } from '../lib/utils.js';
import { ExpandableTableToolbar } from '../table-internals/ExpandableTableToolbar.js';
import { getAriaSort } from '../table-internals/get-aria-sort.js';
import { useBoundedTableHeight } from '../table-internals/use-bounded-table-height.js';
import { useRowKeyboardNavigation } from '../table-internals/use-row-keyboard-navigation.js';
import { collectExpandableRowIds } from './collect-expandable-row-ids.js';
import type { DimeTreeTableProps, TreeRow } from './DimeTreeTable.types.js';
import { buildSiblingGroups } from './row-sibling-position.js';
import { TreeTableRow } from './TreeTableRow.js';
import { useExpandAllTreeRows } from './use-expand-all-tree-rows.js';
import { useTreeRowKeyDown } from './use-tree-row-key-down.js';
import { useTreeTableDevWarnings } from './use-tree-table-dev-warnings.js';
import { useTreeTable } from './use-tree-table.js';

export type { DimeTreeTableProps, TreeColumnDef } from './DimeTreeTable.types.js';

const DEFAULT_INDENT_SIZE = 20;

export function DimeTreeTable<TData extends RowData>(props: Readonly<DimeTreeTableProps<TData>>) {
  const {
    indentColumnId,
    indentSize = DEFAULT_INDENT_SIZE,
    searchColumn,
    searchPlaceholder,
    onSearchChange,
    filters,
    filterChips,
    actions,
    showViewOptions = false,
    showExpandAll = false,
    selectedRowId,
    onSelectRow,
    getRowClassName,
    onRowClick,
    isLoading = false,
    loadingRowCount = 20,
    emptyMessage = 'No results.',
    className,
    maxBodyHeight,
  } = props;

  const { table, hasActiveFilter } = useTreeTable<TData>(props);

  useTreeTableDevWarnings({
    table,
    indentColumnId,
    searchColumn,
    manualFiltering: props.manualFiltering,
    onSearchChange,
    onSelectRow,
    onRowClick,
  });

  // The flattened, depth-ordered, currently-visible row list — what actually renders. Derived from
  // `rows` itself (the same post-filter/sort/expand model), rather than a separate
  // table.getSortedRowModel() call, so the two can never disagree if a manual*/grouping option is
  // ever added to this table's feature set later.
  const rows = table.getRowModel().rows;
  // Every row's sibling set, keyed by parentId — see row-sibling-position.ts for why this must be
  // built from the filtered `rows` above rather than table.getCoreRowModel() or getParentRow().
  const siblingGroups = buildSiblingGroups(rows);
  const visibleColumnCount = table.getVisibleFlatColumns().length;

  const onKeyDown = useTreeRowKeyDown<TData>({ rows, hasActiveFilter, onSelectRow, onRowClick });
  const {
    resolvedActiveRowId,
    registerRowElement,
    setActiveRowId,
    handleRowBlur,
    handleRowKeyDown,
  } = useRowKeyboardNavigation<TreeRow<TData>>({ rows, onKeyDown });

  const coreRows = table.getCoreRowModel().rows;
  const { expandAllRows, collapseAllRows } = useExpandAllTreeRows<TData>({
    coreRows,
    onExpandedChange: table.setExpanded,
  });
  const hasExpandableRows = collectExpandableRowIds(coreRows).length > 0;

  // Reflects the whole tree's expanded state, not just the currently-visible rows: an already-
  // expanded descendant under a collapsed ancestor is invisible in `rows` but must still flip this
  // to Collapse, since DimeTreeTable has no pagination to scope "current page" the way
  // DimeNestedTable's header button does.
  const anyRowExpanded = table.getIsSomeRowsExpanded();

  const showToolbar = Boolean(
    searchColumn || filters || filterChips || actions || showViewOptions || showExpandAll,
  );
  const { containerRef, style: heightStyle, isBounded } = useBoundedTableHeight(maxBodyHeight);

  return (
    <div className={cn('w-full', className)}>
      {showToolbar && (
        <div className="pb-2">
          <ExpandableTableToolbar
            table={table}
            searchColumn={searchColumn}
            searchPlaceholder={searchPlaceholder}
            searchMaxLength={props.searchMaxLength}
            onSearchChange={onSearchChange}
            searchValue={props.searchValue}
            filters={filters}
            filterChips={filterChips}
            actions={actions}
            showViewOptions={showViewOptions}
            showExpandAll={showExpandAll}
            anyRowExpanded={anyRowExpanded}
            hasExpandableRows={hasExpandableRows}
            onExpandAll={expandAllRows}
            onCollapseAll={collapseAllRows}
          />
        </div>
      )}
      <div ref={containerRef} className="rounded-md border border-border overflow-hidden">
        <DimeTable
          role="treegrid"
          containerClassName={isBounded ? 'overflow-auto' : undefined}
          containerStyle={heightStyle}
        >
          <DimeTableHeader
            sticky={isBounded}
            className="[&_tr:not(:last-child)]:border-b-0 [&_tr:not(:last-child)_th]:shadow-none"
          >
            {table.getHeaderGroups().map((headerGroup) => (
              <DimeTableRow key={headerGroup.id} className="hover:bg-transparent">
                {headerGroup.headers.map((header) =>
                  header.isPlaceholder ? (
                    <DimeTableCell key={header.id} colSpan={header.colSpan} />
                  ) : (
                    <DimeTableHead
                      key={header.id}
                      colSpan={header.colSpan}
                      aria-sort={getAriaSort(header.column)}
                      className={header.column.columnDef.meta?.headerClassName}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </DimeTableHead>
                  ),
                )}
              </DimeTableRow>
            ))}
          </DimeTableHeader>
          <DimeTableBody>
            {isLoading && rows.length === 0 ? (
              <SkeletonRows rowCount={loadingRowCount} columnCount={visibleColumnCount} />
            ) : rows.length > 0 ? (
              rows.map((row) => (
                <TreeTableRow<TData>
                  key={row.id}
                  row={row}
                  siblingGroups={siblingGroups}
                  indentColumnId={indentColumnId}
                  indentSize={indentSize}
                  isSelected={selectedRowId !== undefined && selectedRowId === row.id}
                  isActiveRow={resolvedActiveRowId === row.id}
                  rowClassName={getRowClassName?.(row.original)}
                  registerRowElement={registerRowElement}
                  onRowFocus={setActiveRowId}
                  onRowBlur={handleRowBlur}
                  onRowKeyDown={handleRowKeyDown}
                  onToggleExpand={(toggledRow) => {
                    // Inert while a filter is active: expansion display is already fully
                    // overridden by the `true` sentinel (use-tree-table.ts), so toggling here
                    // would silently corrupt the real expansion state instead of doing nothing —
                    // see the comment on hasActiveColumnFilter there.
                    if (!hasActiveFilter) toggledRow.toggleExpanded();
                  }}
                  onSelectRow={
                    onSelectRow ? (selected) => onSelectRow(selected.original) : undefined
                  }
                  onRowClick={onRowClick ? (clicked) => onRowClick(clicked.original) : undefined}
                />
              ))
            ) : (
              <DimeTableRow>
                <DimeTableCell
                  colSpan={visibleColumnCount}
                  className="min-h-24 py-8 text-center text-muted-foreground"
                >
                  {emptyMessage}
                </DimeTableCell>
              </DimeTableRow>
            )}
          </DimeTableBody>
        </DimeTable>
      </div>
    </div>
  );
}
